import { RefObject, useCallback, useRef } from 'react';
import { TimeMs } from '../types';
import { playheadStore } from '../../playback';

type UsePlayheadScrubOptions = {
  /** Pixels per second of the track area at this zoom level. */
  pxPerSecond: number;
  /** Left padding of the track area in px (where t=0 lives visually). */
  startOffset: number;
  /** Max time the playhead can scrub to. */
  maxTime: TimeMs;
  /** Container whose left edge is the reference origin for clientX → time math. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Called once at scrub start (typical use: pause playback). */
  onScrubStart?: () => void;
  /** Called once at scrub end. */
  onScrubEnd?: () => void;
};

/**
 * Seeks the playhead by translating a pointer position into a composition time
 * and writing to `playheadStore`. Designed to drive both:
 *   - Click on the ruler (single-shot seek)
 *   - Drag of the playhead handle (continuous scrub)
 *
 * Both cases use the same `startScrub(e)` handler — a click is just a drag
 * with zero motion before mouseup.
 *
 * Listeners are bound to `window` so the gesture survives the cursor leaving
 * the original element.
 */
export const usePlayheadScrub = ({
  pxPerSecond,
  startOffset,
  maxTime,
  containerRef,
  onScrubStart,
  onScrubEnd
}: UsePlayheadScrubOptions) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  const startScrub = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Cancel any previous scrub defensively.
      cleanupRef.current?.();

      const seekFromClientX = (clientX: number) => {
        const container = containerRef.current;
        if (!container || pxPerSecond <= 0) return;
        const rect = container.getBoundingClientRect();
        const relativeX = clientX - rect.left - startOffset;
        const time = Math.max(0, Math.min(maxTime, (relativeX / pxPerSecond) * 1000));
        playheadStore.set(time);
      };

      seekFromClientX(e.clientX);

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      const handleMove = (ev: MouseEvent) => seekFromClientX(ev.clientX);
      const handleUp = () => cleanup();

      const cleanup = () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        cleanupRef.current = null;
        onScrubEnd?.();
      };

      cleanupRef.current = cleanup;
      onScrubStart?.();
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [pxPerSecond, startOffset, maxTime, containerRef, onScrubStart, onScrubEnd]
  );

  return { startScrub };
};
